import { useCallback, useEffect, useState } from 'react'
import firebase from '../../firebase/compat'
import { useHistory } from 'react-router-dom'
import Modal from '../common/Modal'
import CopyLink from '../common/CopyLink'
import { client } from '../../hooks/Client'
import { useFirestoreOrderBy } from '../../firebase/useFirestore'
import { getQrcodePagePath, getStorytellingOpenPath } from '../../pages/measurementPlan/qrcodeHelpers'
import { STORYTELLING_KINDS as KINDS, STORYTELLING_KIND_LABELS as KIND_LABELS, activityNameById } from '../../helpers/storytellingHelpers/storytellingKinds'

// Het venster 'Deellink' op de storytellingpagina: een link maken waarmee
// iemand zonder inlog storytelling kan insturen, en de bestaande links
// kopiëren, als QR-code delen of aan/uit zetten. De links staan in een
// server-only collectie, dus alles gaat via de callables (storytellingLinks.js).
// AI-CONTEXT: See docs/features/storytelling-deellink.md

/**
 * @typedef {object} StorytellingLinkRow
 * @property {string} code
 * @property {string} name - '' als er geen naam gekozen is
 * @property {Array<'media'|'quote'|'anecdote'>} types
 * @property {Array<string>} activity
 * @property {number | null} expiresAt - epoch-ms
 * @property {number | null} created - epoch-ms
 * @property {boolean} revoked
 * @property {'active'|'revoked'|'expired'|'missing'} state
 */

const STATE_LABELS = { active: 'Aan', revoked: 'Uit', expired: 'Verlopen', missing: 'Onbekend' }
const RETRY = 'Dat lukte niet. Probeer het nog eens.'

/** @param {string} name */
const callable = (name) => firebase.app().functions('europe-west1').httpsCallable(name)

/**
 * 'Tot en met' een datum: het einde van die dag, in de tijd van de gebruiker.
 * @param {string} value - yyyy-mm-dd uit een datumveld, of ''
 * @returns {number | null} epoch-ms
 */
const endOfDay = (value) => {
    if (!value) return null
    const [y, m, d] = value.split('-').map(Number)
    return new Date(y, m - 1, d, 23, 59, 59, 999).getTime()
}

/**
 * @param {number} ms
 * @returns {string} dd-mm-jjjj
 */
const formatDate = (ms) => {
    const d = new Date(ms)
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
}

/**
 * @param {object} props
 * @param {boolean} props.modalOpen - Of het venster open staat
 * @param {(open: boolean) => void} props.setModalOpen - Opent of sluit het venster
 */
const StorytellingShareLinks = ({ modalOpen, setModalOpen }) => {
    const history = useHistory()
    const activities = useFirestoreOrderBy('Activities', 'Position', 'asc')

    // null = laden; een lijst = geladen (ook leeg).
    const [links, setLinks] = useState(/** @type {Array<StorytellingLinkRow> | null} */ (null))
    const [loadFailed, setLoadFailed] = useState(false)
    const [name, setName] = useState('')
    const [types, setTypes] = useState(KINDS.map((k) => k.id))
    const [activityId, setActivityId] = useState('')
    const [until, setUntil] = useState('')
    // '' = niets bezig; 'new' = een link maken; anders de code van de link die aan/uit gaat.
    const [busyKey, setBusyKey] = useState('')
    const [problem, setProblem] = useState('')
    // De zojuist gemaakte link, bovenaan in beeld (testronde 2026-10-30, B3).
    const [created, setCreated] = useState(/** @type {{code: string, name: string} | null} */ (null))
    const busy = Boolean(busyKey)

    const load = useCallback(async () => {
        try {
            const { data } = await callable('listStorytellingLinks')({ client })
            setLinks(data.links)
            setLoadFailed(false)
        } catch (error) {
            console.error('StorytellingShareLinks: lijst laden mislukt', error)
            setLoadFailed(true)
        }
    }, [])

    useEffect(() => {
        if (modalOpen) load()
    }, [modalOpen, load])

    /**
     * Eén handeling tegelijk, en de lijst daarna opnieuw van de server.
     * @param {string} key - Wat er bezig is ('new' of de code van een link)
     * @param {() => Promise<void>} action
     */
    const run = async (key, action) => {
        if (busy) return
        setBusyKey(key)
        setProblem('')
        try {
            await action()
            await load()
        } catch (error) {
            console.error('StorytellingShareLinks: handeling mislukt', error)
            setProblem(error?.code === 'invalid-argument' ? error.message : RETRY)
        }
        setBusyKey('')
    }

    const createLink = () => run('new', async () => {
        const trimmed = name.trim()
        const { data } = await callable('createStorytellingLink')({
            client,
            name: trimmed,
            // In vaste volgorde, wat er ook eerst aangevinkt werd.
            types: KINDS.map((k) => k.id).filter((id) => types.includes(id)),
            activity: activityId ? [activityId] : [],
            expiresAt: endOfDay(until),
        })
        setCreated({ code: data.code, name: trimmed })
        // Leeg voor een volgende link.
        setName('')
        setTypes(KINDS.map((k) => k.id))
        setActivityId('')
        setUntil('')
    })

    /** @param {string} code @param {boolean} revoked */
    const setRevoked = (code, revoked) => run(code, async () => {
        await callable('setStorytellingLinkRevoked')({ client, code, revoked })
        // Geen 'Link kopiëren' bovenaan voor een link die net uitgezet is.
        if (revoked) setCreated((prev) => (prev?.code === code ? null : prev))
    })

    /** @param {string} code */
    const openQrcode = (code) => history.push(getQrcodePagePath({ client, questionnaire: code, route: 'storytelling' }))

    /** @param {string} id */
    const toggleType = (id) => setTypes((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]))

    const content = (
        <div className="storytelling-share">
            {created && (
                <section className="storytelling-share-created" role="status" aria-label="Nieuwe link">
                    <p>{created.name ? `Je nieuwe link staat klaar: ${created.name}` : 'Je nieuwe link staat klaar.'}</p>
                    <div className="storytelling-share-row-actions">
                        <CopyLink link={getStorytellingOpenPath(client, created.code)} label="Link kopiëren" />
                        <button type="button" onClick={() => openQrcode(created.code)}>QR-code</button>
                    </div>
                </section>
            )}
            <section className="storytelling-share-new">
                <h3>Nieuwe deellink</h3>
                <label className="storytelling-share-field">
                    <span>Naam (niet verplicht)</span>
                    <input type="text" value={name} maxLength={60} placeholder="Bijvoorbeeld: buurtcafé" onChange={(e) => setName(e.target.value)} />
                </label>
                <fieldset className="storytelling-share-kinds">
                    <legend>Wat mag er ingestuurd worden?</legend>
                    {KINDS.map((k) => (
                        <label key={k.id}>
                            <input type="checkbox" checked={types.includes(k.id)} onChange={() => toggleType(k.id)} />
                            {k.label}
                        </label>
                    ))}
                </fieldset>
                <label className="storytelling-share-field">
                    <span>Activiteit (niet verplicht)</span>
                    <select value={activityId} onChange={(e) => setActivityId(e.target.value)}>
                        <option value="">Geen activiteit</option>
                        {Array.isArray(activities) && activities.map((a) => (
                            <option key={a.ID} value={a.ID}>{a.Activity}</option>
                        ))}
                    </select>
                </label>
                <label className="storytelling-share-field">
                    <span>Geldig tot en met (niet verplicht)</span>
                    <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
                </label>
                <button type="button" onClick={createLink} disabled={busy || types.length === 0}>{busyKey === 'new' ? 'Bezig…' : 'Link maken'}</button>
            </section>

            {problem && <p className="storytelling-share-problem" role="alert">{problem}</p>}

            <section className="storytelling-share-list">
                <h3>Deellinks</h3>
                {loadFailed && <p className="storytelling-share-problem" role="alert">De deellinks konden niet geladen worden.</p>}
                {!loadFailed && links && links.length === 0 && <p>Nog geen deellinks.</p>}
                {!loadFailed && links && links.map((link) => (
                    <div key={link.code} className="storytelling-share-row" data-testid={`storytelling-link-${link.code}`}>
                        <div className="storytelling-share-row-info">
                            {link.name && <span className="storytelling-share-row-name">{link.name}</span>}
                            <span className="storytelling-share-row-types">{link.types.map((t) => KIND_LABELS[t]).join(', ')}</span>
                            {link.activity.map((id) => activityNameById(activities, id)).filter(Boolean).map((naam) => (
                                <span key={naam} className="storytelling-share-row-activity">{naam}</span>
                            ))}
                            {link.expiresAt && <span>{`Tot en met ${formatDate(link.expiresAt)}`}</span>}
                            <span className={`storytelling-share-state storytelling-share-state--${link.state}`}>{STATE_LABELS[link.state]}</span>
                        </div>
                        <div className="storytelling-share-row-actions">
                            {link.state === 'active' && (
                                <>
                                    <CopyLink link={getStorytellingOpenPath(client, link.code)} label="Link kopiëren" />
                                    <button type="button" onClick={() => openQrcode(link.code)}>QR-code</button>
                                </>
                            )}
                            {link.state === 'active' && (
                                <button type="button" disabled={busy} onClick={() => setRevoked(link.code, true)}>{busyKey === link.code ? 'Bezig…' : 'Uitzetten'}</button>
                            )}
                            {link.state === 'revoked' && (
                                <button type="button" disabled={busy} onClick={() => setRevoked(link.code, false)}>{busyKey === link.code ? 'Bezig…' : 'Aanzetten'}</button>
                            )}
                        </div>
                    </div>
                ))}
            </section>
        </div>
    )

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            pageHeader="Deellink"
            content={content}
            hideConfirm
            cancelLabel="Sluiten"
        />
    )
}

export default StorytellingShareLinks
